import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Paper, Stack } from '@mui/material'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { HTMLEditor, Section, TextField } from '~/components'
import { bshSlice } from '~/store'
import Field from './Field'
import { useCopyViewerText, useParagraph } from './hooks'
import Paragraph from './Paragraph'
import ParagraphContext from './ParagraphContext'
import Title from './Title'

const CustomParagraph = (props) => {
  const { paragraph: paragraphProp, variant, onEditing } = props

  const { t } = useTranslation()

  const ref = useRef()

  const paragraphContainer = useParagraph({
    paragraph: paragraphProp,
    ref,
    onEditing,
  })
  const { canEdit, editable, paragraph, onPropertyChange } = paragraphContainer
  const copyViewerText = useCopyViewerText(canEdit)
  const viewerFieldSx = canEdit ? undefined : { cursor: 'pointer' }

  const handlePropertyChange = useCallback(
    (property, value) => {
      const shops = _.cloneDeep(paragraph.shops) ?? {}
      shops[variant] = {
        ...shops[variant],
        [property]: value,
      }
      onPropertyChange('shops', shops)
    },
    [onPropertyChange, paragraph.shops, variant]
  )

  const titleContainsEmpCo = useSelector((state) =>
    bshSlice.containsEmpCo(
      state,
      paragraph.shops?.[variant]?.title || paragraph.title
    )
  )

  const textContainsEmpCo = useSelector((state) =>
    bshSlice.containsEmpCo(
      state,
      paragraph.shops?.[variant]?.text || paragraph.text
    )
  )

  return (
    <ParagraphContext.Provider value={paragraphContainer}>
      <Section
        ref={ref}
        key={paragraph.id}
        as={Paper}
        sectionId={`${Paragraph.name}-${paragraph.id}`}
        sx={{
          backgroundColor: 'background.default',
          p: 1,
          '> *': !canEdit || editable ? undefined : { pointerEvents: 'none' },
        }}
        title={<Title readOnly />}
      >
        <Stack spacing={1}>
          {paragraph.type !== 'text' && (
            <Stack spacing={1}>
              <div dangerouslySetInnerHTML={{ __html: paragraph.title }} />
              <Field
                name={`shops.${variant}.title`}
                render={(fieldProps) => (
                  <Box
                    onClick={() =>
                      copyViewerText(
                        paragraph.shops?.[variant]?.title || paragraph.title
                      )
                    }
                    sx={viewerFieldSx}
                  >
                    <TextField
                      editable={editable}
                      highlighted={titleContainsEmpCo}
                      placeholder={t(`paragraph.title`)}
                      size="small"
                      sx={{ display: 'flex' }}
                      value={
                        paragraph.shops?.[variant]?.title ||
                        paragraph.title ||
                        ''
                      }
                      {...fieldProps}
                      onChange={({ target: { value } }) => {
                        handlePropertyChange('title', value)
                      }}
                    />
                  </Box>
                )}
              />
            </Stack>
          )}
          {paragraph.type !== 'title' && (
            <Stack spacing={1}>
              <div dangerouslySetInnerHTML={{ __html: paragraph.text }} />
              <Field
                name={`shops.${variant}.text`}
                render={(fieldProps) => (
                  <Box
                    onClick={() =>
                      copyViewerText(
                        paragraph.shops?.[variant]?.text || paragraph.text
                      )
                    }
                    sx={viewerFieldSx}
                  >
                    <HTMLEditor
                      disabled={!editable}
                      highlighted={textContainsEmpCo}
                      text={paragraph.shops?.[variant]?.text || paragraph.text}
                      {...fieldProps}
                      onChange={(e) =>
                        handlePropertyChange('text', e.target.value)
                      }
                    />
                  </Box>
                )}
              />
            </Stack>
          )}
        </Stack>
      </Section>
    </ParagraphContext.Provider>
  )
}

CustomParagraph.propTypes = {
  paragraph: PropTypes.object.isRequired,
  variant: PropTypes.string.isRequired,
  onEditing: PropTypes.func,
}

export default CustomParagraph
